import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import { Shield, TestTube, Trash, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * Seed Proxy — a single rotating proxy endpoint used for ALL Gmail traffic
 * on seed mailboxes: OAuth code→token exchange, userinfo, refresh tokens,
 * per-mailbox test pings, and Gmail label polling for Inbox Placement.
 *
 * The user runs a rotating proxy service on their side — one static
 * host:port that rotates IPs upstream. No per-seed sticky assignment.
 */
export default function SeedProxy() {
  const [cfg, setCfg] = useState({
    enabled: true, protocol: "http", host: "", port: "",
    username: "", password: "",
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/seed-proxy");
      setStatus(data);
      if (data.configured) {
        setCfg((c) => ({
          ...c,
          enabled: data.enabled,
          protocol: data.protocol,
          host: data.host,
          port: String(data.port),
          username: data.username,
          password: "", // never returned by server
        }));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg.host.trim() || !cfg.port) { toast.error("Host + port required"); return; }
    setSaving(true);
    try {
      await api.put("/seed-proxy", {
        enabled: !!cfg.enabled,
        protocol: cfg.protocol,
        host: cfg.host.trim(),
        port: Number(cfg.port),
        username: cfg.username || "",
        password: cfg.password || "",
      });
      toast.success("Proxy saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/seed-proxy/test");
      toast[data.ok ? "success" : "error"](data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const clear = async () => {
    if (!confirm("Clear the seed proxy? Gmail traffic will fall back to direct connections.")) return;
    try {
      await api.delete("/seed-proxy");
      toast.success("Cleared");
      setCfg({ enabled: true, protocol: "http", host: "", port: "", username: "", password: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="seed-proxy-page">
      <div className="mb-6">
        <h1 className="text-2xl font-mono uppercase tracking-wider flex items-center gap-2">
          <Shield size={22} weight="bold" className="text-[#E1FF00]" /> Seed Proxy
        </h1>
        <p className="text-xs text-[#71717A] mt-1 font-mono max-w-2xl leading-relaxed">
          One rotating proxy endpoint used for every Gmail request against your seed mailboxes —
          OAuth handshake, token refresh, and inbox-placement polling.
          Google flags datacenter IPs and IP hops; keep this consistent and healthy.
        </p>
      </div>

      {loading ? (
        <div className="text-[#71717A] font-mono">[ loading... ]</div>
      ) : (
        <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-6 space-y-4">
          <StatusRow status={status} />

          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-1">
              <label className="micro block mb-1">Protocol</label>
              <select value={cfg.protocol} onChange={(e) => setCfg({ ...cfg, protocol: e.target.value })}
                data-testid="seed-proxy-protocol"
                className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none">
                <option value="http">http</option>
                <option value="https">https</option>
                <option value="socks5">socks5</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="micro block mb-1">Host</label>
              <input value={cfg.host} onChange={(e) => setCfg({ ...cfg, host: e.target.value })}
                placeholder="rotating.proxy.example.com" data-testid="seed-proxy-host"
                className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="micro block mb-1">Port</label>
              <input type="number" value={cfg.port} onChange={(e) => setCfg({ ...cfg, port: e.target.value })}
                placeholder="8080" data-testid="seed-proxy-port"
                className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="micro block mb-1">Username</label>
              <input value={cfg.username} onChange={(e) => setCfg({ ...cfg, username: e.target.value })}
                data-testid="seed-proxy-user"
                className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="micro block mb-1">
                Password
                {status?.configured && (
                  <span className="ml-2 text-[10px] text-[#71717A]">
                    (stored: {status.password_masked || "—"}, leave blank to keep)
                  </span>
                )}
              </label>
              <input type="password" value={cfg.password} onChange={(e) => setCfg({ ...cfg, password: e.target.value })}
                data-testid="seed-proxy-pass"
                className="w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs text-white font-mono focus:border-[#E1FF00] focus:outline-none" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs font-mono cursor-pointer">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="seed-proxy-enabled" />
            <span>Route all seed-mailbox Gmail traffic through this proxy</span>
          </label>

          <div className="flex gap-2 pt-2">
            <BtnPrimary onClick={save} disabled={saving} testid="seed-proxy-save">
              {saving ? "Saving..." : "Save"}
            </BtnPrimary>
            <BtnSecondary onClick={test} disabled={testing || !status?.configured} testid="seed-proxy-test">
              <TestTube size={13} weight="bold" /> {testing ? "Testing..." : "Test connection"}
            </BtnSecondary>
            {status?.configured && (
              <BtnDestructive onClick={clear} testid="seed-proxy-clear">
                <Trash size={13} weight="bold" /> Clear
              </BtnDestructive>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 text-[11px] text-[#71717A] font-mono border border-[#2A2A2A] bg-[#0A0A0A] p-4 leading-relaxed">
        <div className="uppercase text-white mb-2">What this proxy covers</div>
        <div>• OAuth code → token exchange during "Connect Gmail" and "Batch Connect"</div>
        <div>• Access-token refresh cycles (every ~55 minutes per seed)</div>
        <div>• Per-mailbox test-ping (Test button on Seed Gmail page)</div>
        <div>• Gmail label polling for every Inbox Placement result</div>
        <div className="mt-2 text-[#71717A]">
          If disabled or unset, all these calls go direct from the server IP.
        </div>
      </div>
    </div>
  );
}

function StatusRow({ status }) {
  if (!status?.configured) {
    return (
      <div className="flex items-center gap-2 text-[11px] font-mono text-[#71717A] border border-[#2A2A2A] px-3 py-2">
        <Warning size={14} weight="bold" /> No proxy configured — Gmail traffic goes direct.
      </div>
    );
  }
  const okColor = status.last_test_ok === true ? "#00E5FF" : status.last_test_ok === false ? "#FF3B30" : "#71717A";
  const label = status.last_test_ok === true ? "healthy" : status.last_test_ok === false ? "broken" : "untested";
  return (
    <div className="flex items-center gap-2 text-[11px] font-mono border border-[#2A2A2A] px-3 py-2">
      <CheckCircle size={14} weight="fill" style={{ color: okColor }} />
      <span style={{ color: okColor }}>{label}</span>
      <span className="text-[#71717A]">— {status.protocol}://{status.host}:{status.port}</span>
      {status.enabled ? (
        <span className="ml-auto text-[10px] text-[#00E5FF]">enabled</span>
      ) : (
        <span className="ml-auto text-[10px] text-[#FF3B30]">disabled</span>
      )}
    </div>
  );
}
